import React, { useState, useEffect } from 'react';
import API from '../../../pages/Api';
import { toast } from 'react-toastify';
import { FaSearch } from 'react-icons/fa';
import TableContainer from '../../common/Tablecontainer'; // 💡 Synergizes architecture layers safely
// 💡 Mapped Semantic global constants keys safely
import { gender as genderMapping, residence as residenceMapping } from '../../../constant/constants';

const RegistrationReport = () => {
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Controlled parameter configuration logic
    const [filterType, setFilterType] = useState('All');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [list, setList] = useState([]);
    const [loadingCards, setLoadingCards] = useState(false);
    const [mobilePage, setMobilePage] = useState(1);
    const [mobileTotalPages, setMobileTotalPages] = useState(1);

    const loadRegistrationReport = async (
        page = mobilePage,
        search = searchQuery
    ) => {
        try {

            setLoadingCards(true);

            const token = localStorage.getItem("access_token");

            const headers = token
                ? {
                    Authorization: `Bearer ${token}`
                }
                : {};

            const res = await API.get(
                "admin/registration-report/",
                {
                    params: {
                        page,
                        search,
                        type: filterType,
                        fromDate,
                        toDate
                    },
                    headers
                }
            );

            setList(res.data.results || []);
            setMobileTotalPages(Math.ceil((res.data.count || 0) / 10));

        } finally {

            setLoadingCards(false);

        }
    };
    const [isMobile, setIsMobile] = useState(
        window.innerWidth < 768
    );

    useEffect(() => {

        const resize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", resize);

        return () => {
            window.removeEventListener("resize", resize);
        };

    }, []);
    useEffect(() => {

        if (!isMobile) return;

        const timer = setTimeout(() => {

            setMobilePage(1);

            loadRegistrationReport(1, searchQuery);

        },400);

        return ()=>clearTimeout(timer);

    },[searchQuery,filterType,fromDate,toDate]);

    useEffect(()=>{

        if(!isMobile) return;

        loadRegistrationReport(
            mobilePage,
            searchQuery
        );

    },[mobilePage,filterType,fromDate,toDate]);

    const handleFetchReportAction = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        try {
           
            const res = await API.get(`admin/registration-report/`, {
                params: {
                    type: filterType, // Sends 'All', 'India', or 'Other Country' safely
                    fromDate: fromDate || '',  
                    toDate: toDate || ''      
                }
            });
            setReportData(res.data);

            if (isMobile) {

                setMobilePage(1);

                loadRegistrationReport(
                    1,
                    searchQuery
                );

            }
        } catch (err) {
            console.error("Failure communicating network evaluation query sets:", err);
            toast.error("API failed to harvest report logs matrix.");
        } finally {
            setLoading(false);
        }
    };

    // 💡 FIX 1: 'filterType' 
    useEffect(() => {
        loadRegistrationReport();
    }, [filterType]); 

    const gridHeaders = ['Full Name', 'Email Identifier', 'Mobile No', 'Location/City', 'Gender', 'Joined Date', 'Residence'];

    const handlePrintGridAction = () => {
        window.print();
    };
    return (
        <div className="p-6 bg-[#F8FAFC] min-h-screen w-full flex flex-col items-center font-sans animate-fadeIn select-none">
            <div className="w-full max-w-8xl mx-auto flex flex-col space-y-6 px-2">
                
                {/* ─── BLOCK 1: CREATIVE HEADER CONTROL BAR PANEL ─── */}
                <div className="w-full text-center rounded-lg">
                    <h2 className="text-center text-4xl font-light text-gray-700 mb-8 tracking-tight">
                        Registration Report
                    </h2>
                </div>

                {/* ─── BLOCK 2: PARAMETERS SELECTOR CONTROL BOX MATRIX ─── */}
                <form onSubmit={handleFetchReportAction} className="w-full bg-white border border-gray-300 px-6 pb-6 pt-2 rounded relative shadow-xs flex flex-col space-y-4">
                    <span className="absolute -top-2.5 left-4 bg-[#F8FAFC] px-2 text-[11px] font-bold text-gray-700 tracking-wide">
                        Registration Report
                    </span>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-xs font-bold uppercase text-gray-700">
                        
                        {/* Control Input Element 1: Select Type Dropdown Parameter */}
                        <div className="flex
                        flex-col
                        md:flex-row
                        gap-2">
                            <label className="w-full md:w-[30%] text-right pr-2">Select Type(<span className="text-red-500">*</span>) :</label>
                            <select 
                                className="flex-1 p-2 border border-gray-300 rounded-md outline-none bg-gray-50 font-bold text-[#032167] cursor-pointer hover:border-blue-400 h-9 transition-all shadow-sm"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)} // 👈 Hooks directly into useEffect trigger chain
                            >
                                <option value="All">All Registered Profiles</option>
                                <option value="India">India Residence Origin</option>
                                <option value="Other Country">Other Country / Non-India</option>
                            </select>
                        </div>

                        {/* Control Input Element 2: From Date Input Parameter */}
                        <div className="flex items-center gap-2">
                            <label className="w-full md:w-[30%] text-right pr-2">From Date(<span className="text-red-500">*</span>) :</label>
                            <input 
                                type="date"
                                className="flex-1 p-2 border border-gray-300 rounded-md outline-none bg-gray-50 font-semibold text-gray-700 hover:border-blue-400 h-9 transition-all shadow-sm"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>

                        {/* Control Input Element 3: To Date Input Parameter */}
                        <div className="flex items-center gap-2">
                            <label className="w-full md:w-[30%] text-right pr-2">To Date(<span className="text-red-500">*</span>) :</label>
                            <input 
                                type="date"
                                className="flex-1 p-2 border border-gray-300 rounded-md outline-none bg-gray-50 font-semibold text-gray-700 hover:border-blue-400 h-9 transition-all shadow-sm"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>

                    </div>

                    {/* Central Button Operational Row Dispatch Block */}
                    <div className="flex justify-center pt-4 border-t gap-4">
                        <button 
                            type="submit" 
                            className="px-10 py-2.5 bg-[#2563EB] text-white text-xs font-bold uppercase tracking-wider rounded-md hover:bg-blue-800 transition-all cursor-pointer border-none shadow-md active:scale-95"
                        >
                            Search 
                        </button>
                    </div>
                </form>

                {/* ─── BLOCK 3: DATA GRID OUTPUT PANEL AREA ─── */}
                <div className="w-full bg-white rounded-xl shadow-sm overflow-visible md:overflow-hidden border border-gray-200">

                    {/* <div className="w-full p-2.5 bg-white border-b border-gray-300 flex justify-end items-center print:hidden">
                        <button 
                            type="button"
                            onClick={handlePrintGridAction}
                            className="px-6 py-1.5 bg-[#2563EB] text-white font-semibold text-xs tracking-wide rounded hover:bg-blue-800 transition-colors cursor-pointer border-none shadow-md active:scale-95"
                        >
                            Print
                        </button>
                    </div> */}
                    <div className="hidden md:block">
                    <TableContainer
                        columns={gridHeaders}
                        apiUrl="admin/registration-report/"
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        searchPlaceholder="Type name or email to filter inside active grid instantly..."
                        customActions={
                            <button 
                                type="button"
                                onClick={handlePrintGridAction}
                                className="px-6 py-1.5 bg-[#2563EB] text-white font-semibold text-xs tracking-wide rounded hover:bg-blue-800 transition-colors cursor-pointer border-none shadow-md active:scale-95">
                                Print
                            </button>
                        }
                        renderRow={(item, index, srNo) => (
                            <tr key={item.userid || index} className="border-b border-gray-200 hover:bg-gray-50/80 transition-colors h-auto text-gray-700 text-xs font-medium bg-white">
                                <td className="py-3 px-2 border-r bg-gray-50/30 font-bold text-gray-400 w-[6%] text-center select-none">{srNo}</td>
                                <td className="py-3 px-2 border-r text-left pl-5 font-bold text-gray-800 tracking-wide uppercase">{item.name || 'N/A'}</td>
                                <td className="py-3 px-2 border-r text-left pl-5 text-blue-600 lowercase font-mono font-medium">{item.emailid}</td>
                                <td className="py-3 px-2 border-r text-center tracking-normal font-semibold text-gray-600">{item.mobileno || '-'}</td>
                                <td className="py-3 px-2 border-r text-left pl-4 normal-case font-normal text-gray-500">{item.location || 'N/A'}</td>
                                
                                {/* 💡 Mapped precisely with Gender Fallback Matrix to enforce strict ' - ' on blank grids */}
                                <td className="py-3 px-2 border-r text-center uppercase font-normal text-[#032167]">
                                    {genderMapping[item.gender] || item.gender || ' - '}
                                </td>
                                
                                <td className="py-3 px-2 border-r text-center font-bold tracking-tight text-teal-600">
                                    {item.formatted_joined_date || 'N/A'}
                                </td>
                                
                                {/* 💡 Mapped precisely with Residence Fallback Matrix to enforce strict 'N/A' on blank grids */}
                                <td className="py-3 px-2 text-center uppercase font-semibold text-[#032167] w-28">
                                    {residenceMapping[item.residence] || item.residence || 'N/A'}
                                </td>
                            </tr>
                        )}
                        
                    />
                    </div>
                    <div className="md:hidden mt-4">

                    <input
                    type="text"
                    value={searchQuery}
                    onChange={(e)=>setSearchQuery(e.target.value)}
                    placeholder="Search registration..."
                    
                    className="
                    w-full
                    max-w-full
                    border
                    rounded-lg
                    p-3
                    text-sm
                    outline-none
                    focus:border-blue-500
                    "
                    />

                    </div>
                    <div className="md:hidden space-y-4 mt-4">

                        {loadingCards ? (

                            <div className="text-center py-10">
                                Loading...
                            </div>

                        ) : list.length === 0 ? (

                            <div className="text-center py-10 text-gray-500">
                                No Records Found
                            </div>

                        ) : (

                            list.map((item) => (

                                <div
                                    key={item.userid}
                                    className="
                                    bg-white
                                    rounded-xl
                                    shadow-sm
                                    border
                                    p-4
                                    w-full
                                    max-w-full
                                    min-w-0
                                    overflow-hidden
                                    "
                                >

                                    <div className="space-y-3 w-full min-w-0 overflow-hidden">

                                        <div>
                                            <p className="text-xs text-gray-500">Name</p>
                                            <p className="font-bold break-words">
                                                {item.name || "N/A"}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500">Email</p>
                                            <p className="break-all text-blue-700 text-sm">
                                                {item.emailid || "N/A"}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500">Mobile</p>
                                            <p>{item.mobileno || "-"}</p>
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500">Location</p>
                                            <p className="break-words">
                                                {item.location || "N/A"}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500">Gender</p>
                                            <p>
                                                {genderMapping[item.gender] || item.gender || "-"}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500">Joined Date</p>
                                            <p>
                                                {item.formatted_joined_date || "N/A"}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500">Residence</p>
                                            <p>
                                                {residenceMapping[item.residence] || item.residence || "N/A"}
                                            </p>
                                        </div>

                                    </div>

                                </div>

                            ))

                        )}

                    </div>
                    <div className="md:hidden flex justify-between items-center py-5">

                    <button
                    disabled={mobilePage===1}
                    onClick={()=>setMobilePage(p=>p-1)}
                    className="
                    px-4
                    py-2
                    border
                    rounded
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                    "
                    >
                    Previous
                    </button>

                    <span>
                    Page {mobilePage} of {mobileTotalPages}
                    </span>

                    <button
                    disabled={mobilePage===mobileTotalPages}
                    onClick={()=>setMobilePage(p=>p+1)}
                    className="
                    px-4
                    py-2
                    border
                    rounded
                    disabled:opacity-40
                    disabled:cursor-not-allowed
                    "
                    >
                    Next
                    </button>

                    </div>
                </div>

            </div>
        </div>
    );
};

export default RegistrationReport;